'use client';

import { apiFetchWithTimeout } from '@/lib/api';
import { consumeAuthMeBootstrap } from '@/lib/auth-me-bootstrap';
import type { AppUser } from '@tracedocuments/types';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { createContext, useContext, useMemo, useState } from 'react';
import { queryKeys } from './query-keys';
import { STALE_TIMES } from './query-stale-times';
export type { AppUser } from '@tracedocuments/types';

type AppDataContextValue = {
  user: AppUser | null;
  /** True while the initial /me fetch or a remount refetch is in flight. */
  loading: boolean;
  refreshUser: () => Promise<AppUser | null>;
  applyUser: (user: AppUser | null) => void;
};

const AppDataContext = createContext<AppDataContextValue | undefined>(undefined);

async function fetchAuthMe(): Promise<AppUser | null> {
  try {
    const res = await apiFetchWithTimeout('/api/auth/me');
    if (!res.ok) {
      return null;
    }
    return (await res.json()) as AppUser;
  } catch (err) {
    if (err instanceof Error && err.name === 'AuthSessionError') {
      return null;
    }
    throw err;
  }
}

export function AppDataProvider({ children }: { children: React.ReactNode }) {
  const queryClient = useQueryClient();
  const [bootstrapUser] = useState(() => consumeAuthMeBootstrap());
  const { data: user = null, isLoading, refetch } = useQuery({
    queryKey: queryKeys.auth.me(),
    queryFn: fetchAuthMe,
    staleTime: STALE_TIMES.authMe,
    retry: false,
    initialData: bootstrapUser,
    initialDataUpdatedAt: bootstrapUser ? Date.now() : undefined,
  });

  const value = useMemo<AppDataContextValue>(
    () => ({
      user,
      // Cache-first: do not treat background refetch as shell-blocking loading.
      loading: isLoading && user == null,
      refreshUser: async () => {
        const result = await refetch();
        return result.data ?? null;
      },
      applyUser: (next) => {
        queryClient.setQueryData(queryKeys.auth.me(), next);
      },
    }),
    [isLoading, queryClient, refetch, user],
  );

  return <AppDataContext.Provider value={value}>{children}</AppDataContext.Provider>;
}

export function useAppUser(): { user: AppUser | null; loading: boolean } {
  const context = useContext(AppDataContext);
  if (!context) {
    throw new Error('useAppUser must be used within AppDataProvider');
  }

  return { user: context.user, loading: context.loading };
}

export function useAppData(): AppDataContextValue {
  const context = useContext(AppDataContext);
  if (!context) {
    throw new Error('useAppData must be used within AppDataProvider');
  }

  return context;
}
